import React, { useEffect, useState, useMemo } from "react";
import { 
    Row, 
    Col, 
    Typography, 
    Select, 
    Avatar, 
    Modal, 
    Tag, 
    Button, 
    Space, 
    Input,
    Card,
    Table,
    Tooltip,
    Calendar,
    Divider
} from "antd";
import { 
    UserOutlined, 
    CalendarOutlined, 
    VideoCameraOutlined, 
    EyeOutlined, 
    ClockCircleOutlined,
    SearchOutlined,
    DownloadOutlined,
    MailOutlined,
    PhoneOutlined,
    MessageOutlined,
    AppstoreOutlined,
    UnorderedListOutlined,
    InfoCircleOutlined,
    GlobalOutlined,
    LeftOutlined,
    RightOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import moment from "moment";
import "./CalendarPage.css";
import calendar from "../../../services/calendar";
import AppointmentsDetailModal from "./AppointmentsDetailModal";
import user, { ccmCoordinatorService, physicianService } from "../../../services/user";
import { extractToken } from "../../../utils/localStorage";
import { globalRoles } from "../../../utils/roles";
import { ReminderButton } from "../journeys/components/ClinicalJourneyCard";

const { Title, Text } = Typography;
const { Option } = Select;

const DashboardWrapper = styled.div`
    padding: 32px;
    background-color: var(--primary-bg);
    min-height: 100vh;
    color: var(--primary-text-color);
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 32px;
`;

const StatCard = styled(Card)`
    border-radius: 12px;
    border: 1px solid var(--border-color);
    background: var(--secondary-bg);
    height: 100%;
    .ant-card-body {
        padding: 24px;
    }
    .stat-title {
        color: var(--secondary-text-color);
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 8px;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .stat-value {
        font-size: 32px;
        font-weight: 700;
        color: var(--primary-text-color);
    }
    .stat-footer {
        margin-top: 8px;
        font-size: 12px;
        color: var(--secondary-text-color);
    }
`;

const FilterCard = styled(Card)`
    border-radius: 12px;
    border: 1px solid var(--border-color);
    background: var(--primary-bg);
    margin-bottom: 24px;
    .ant-card-body {
        padding: 24px;
    }
`;

const StyledTable = styled(Table)`
    .ant-table {
        background: transparent !important;
        color: var(--primary-text-color) !important;
    }
    .ant-table-thead > tr > th {
        background: var(--primary-bg) !important;
        color: var(--secondary-text-color) !important;
        border-bottom: 1px solid var(--border-color) !important;
        font-weight: 600;
        text-transform: capitalize;
    }
    .ant-table-tbody > tr > td {
        border-bottom: 1px solid var(--border-color) !important;
        padding: 16px 8px !important;
    }
    .ant-table-tbody > tr:hover > td {
        background: var(--hover-bg) !important;
    }
`;

const ViewToggle = styled.div`
    display: flex;
    background: var(--secondary-bg);
    padding: 4px;
    border-radius: 8px;
    border: 1px solid var(--border-color);
    
    .toggle-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        border-radius: 6px;
        cursor: pointer;
        font-weight: 600;
        transition: all 0.2s;
        
        &.active {
            background: #7c3aed;
            color: white;
        }
        
        &:not(.active) {
            color: var(--secondary-text-color);
            &:hover {
                background: var(--border-color);
            }
        }
    }
`;

const CalendarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 16px;
    margin-top: 24px;
`;

const CalendarDayCard = styled.div`
    background: var(--secondary-bg);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    padding: 16px;
    min-height: 140px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    transition: all 0.2s;
    position: relative;
    
    &:hover {
        border-color: #7c3aed;
        box-shadow: 0 4px 12px var(--shadow-color);
    }

    &.has-appointment {
        background: var(--primary-bg);
    }

    .day-header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 12px;
        color: var(--secondary-text-color);
        font-size: 12px;
        font-weight: 600;
    }

    .appointment-content {
        .patient-name {
            font-weight: 700;
            font-size: 14px;
            color: var(--primary-text-color);
            margin-bottom: 2px;
        }
        .appointment-type {
            font-size: 12px;
            color: var(--secondary-text-color);
            margin-bottom: 8px;
        }
    }

    .status-badge {
        display: inline-flex;
        align-items: center;
        background: #fee2e2;
        color: #ef4444;
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
    }

    .status-dot {
        position: absolute;
        top: 16px;
        right: 16px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #ef4444;
    }
`;

const CalendarPage = () => {
    const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
    const [calendarData, setCalendarData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearchText, setDebouncedSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("All Statuses");
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [userRole, setUserRole] = useState("Physician");
    const [allPhysicians, setAllPhysicians] = useState([]);
    const [ccmCoordinators, setCCMCoordinators] = useState([]);
    const [selectedDate, setSelectedDate] = useState(moment());
    const user = extractToken();


    const fetchData = async (searchQuery = "", status = "") => {
        setLoading(true);
        try {
            const response = await calendar.getCalender(searchQuery, status);
            console.log("📅 Calendar API Response:", response);
            console.log("📅 Calendar Data:", response?.data || []);
            console.log("🔍 Search Query:", searchQuery);
            console.log("🎯 Status Filter:", status);
            setCalendarData(response?.data || []);
        } catch (error) {
            console.error("Error fetching calendar data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        fetchData();
    }, []);

    // Debounce search text - wait 500ms after user stops typing
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchText]);

    // Fetch data when debounced search text or status filter changes
    useEffect(() => {
        if (debouncedSearchText !== undefined) {
            fetchData(debouncedSearchText, statusFilter);
        }
    }, [debouncedSearchText, statusFilter]);

    const processedData = useMemo(() => {
        const processed = calendarData.flatMap(dayItem => 
            dayItem.patientsDetail.map(patient => ({
                ...patient,
                date: dayItem.date,
                scheduledBy: dayItem.scheduledBy,
                totalAssigned: dayItem.totalAssigned,
                watchedCount: dayItem.watchedCount,
                key: `${dayItem.date}-${patient.patientId}`
            }))
        );
        console.log("📊 Processed Calendar Data:", processed);
        console.log("📊 Total Processed Records:", processed.length);
        return processed;
    }, [calendarData]);

    // Both search and status filters are now handled by backend API
    const filteredData = useMemo(() => {
        return processedData;
    }, [processedData]);

    const stats = {
        totalSent: processedData.length,
        watched: processedData.filter(p => p.videos?.every(v => v.isWatched)).length,
        actionNeeded: processedData.filter(p => p.videos?.some(v => !v.isWatched)).length,
        partialViews: Math.floor(processedData.length * 0.3) // Just for demo
    };

    const columns = [
        {
            title: 'Patient',
            key: 'patient',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 700, color: 'var(--primary-text-color)' }}>{record.patientName}</div>
                    <div style={{ fontSize: 12, color: 'var(--secondary-text-color)' }}>{record.email || 'no-email@example.com'}</div>
                    <div style={{ fontSize: 12, color: 'var(--secondary-text-color)' }}>(555) 123-4567</div>
                </div>
            )
        },
        {
            title: 'Video',
            key: 'video',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 600, color: 'var(--primary-text-color)' }}>{record.videos?.[0]?.videoName || 'No Video'}</div>
                    <div style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>URGENT - appointment tomorrow</div>
                </div>
            )
        },
        {
            title: 'Appointment',
            key: 'appointment',
            render: (_, record) => (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                        <CalendarOutlined /> {new Date(record.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--secondary-text-color)' }}>Arthritis Treatment</div>
                </div>
            )
        },
        {
            title: 'Sent Date',
            key: 'sentDate',
            render: (_, record) => (
                <div style={{ color: 'var(--primary-text-color)' }}>
                    {new Date(record.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
            )
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const watched = record.videos?.every(v => v.isWatched);
                return watched ? (
                    <Tag color="success" style={{ borderRadius: 12, padding: '2px 12px' }}>Watched 100%</Tag>
                ) : (
                    <Tag color="error" style={{ borderRadius: 12, padding: '2px 12px' }}>Not Watched</Tag>
                );
            }
        },
        {
            title: 'Days Until',
            key: 'daysUntil',
            render: () => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#ef4444', fontWeight: 600 }}>
                    <ClockCircleOutlined /> 1d
                </div>
            )
        },
        {
            title: 'Scheduler',
            key: 'scheduler',
            render: (_, record) => (
                <div style={{ color: 'var(--primary-text-color)' }}>{record.scheduledBy}</div>
            )
        },
        // {
        //     title: 'Actions',
        //     key: 'actions',
        //     render: () => (
        //         <Space size="middle">
        //             <Tooltip title="Call"><PhoneOutlined style={{ color: 'var(--secondary-text-color)', cursor: 'pointer' }} /></Tooltip>
        //             <Tooltip title="Email"><MailOutlined style={{ color: 'var(--secondary-text-color)', cursor: 'pointer' }} /></Tooltip>
        //             <Tooltip title="Message"><MessageOutlined style={{ color: 'var(--secondary-text-color)', cursor: 'pointer' }} /></Tooltip>
        //         </Space>
        //     )
        // }
    ];

    // Month navigation functions
    const handlePreviousMonth = () => {
        setSelectedDate(prev => moment(prev).subtract(1, 'month'));
    };

    const handleNextMonth = () => {
        setSelectedDate(prev => moment(prev).add(1, 'month'));
    };

    const handleYearChange = (year) => {
        setSelectedDate(prev => moment(prev).year(year));
    };

    const handleMonthChange = (month) => {
        setSelectedDate(prev => moment(prev).month(month));
    };

    // Generate year options (current year ± 10 years)
    const currentYear = moment().year();
    const yearOptions = [];
    for (let i = currentYear - 10; i <= currentYear + 10; i++) {
        yearOptions.push(i);
    }

    // Month options
    const monthOptions = moment.months();

    const handlePatientClick = (patient) => {
        setSelectedPatient(patient);
        setIsModalVisible(true);
    };

    const dateCellRender = (value) => {
        const dateStr = value.format('YYYY-MM-DD');
        const dayAppointments = processedData.filter(d => d.date === dateStr);
        
        return (
            <div className="calendar-cell-content">
                {dayAppointments.map((app, idx) => (
                    <div 
                        key={idx} 
                        className="calendar-appointment-pill"
                        onClick={(e) => {
                            e.stopPropagation();
                            handlePatientClick(app);
                        }}
                    >
                        <div className="dot"></div>
                        <span className="name">{app.patientName}</span>
                    </div>
                ))}
            </div>
        );
    };

    const handleUserRoleChange = (value) => {
        setUserRole(value);
    };  

  

     const GetAllPhysician = async () => {
        try {
          setLoading(true);
          const response = await physicianService.getAll(0, 1, "", "", "");
          setAllPhysicians(response?.data?.results);
        } catch (error) {
         
        } 
      };

      const GetCCMCoordinators = async () => {
          try {
            const response = await ccmCoordinatorService.getAll(0, 1, "", "", "");
           
            setCCMCoordinators(response?.data?.results);
           
          } catch (error) {
            setCCMCoordinators([]);
          }
        };

      useEffect(() => {
       if(user?.role === globalRoles.SUPERADMIN) {
        if (userRole === "Physician") GetAllPhysician();
        else GetCCMCoordinators();
       } 
      }, []);

    return (
        <>
        { 
            isModalVisible && (
                <AppointmentsDetailModal
                    visible={isModalVisible}
                    setVisible={setIsModalVisible}
                    selectedPatient={selectedPatient}
                />
            )
        }
        
        <DashboardWrapper className="calendar-page-container">
            <HeaderSection>
                <div>
                    <Title level={2} style={{ margin: 0, color: 'var(--primary-text-color)', fontWeight: 800 }}>Video Tracking Dashboard</Title>
                    <Text style={{ color: 'var(--secondary-text-color)', fontSize: 16 }}>Monitor patient education video engagement and upcoming appointments</Text>
                </div>
                {/* <Button icon={<DownloadOutlined />} size="large" style={{ borderRadius: 8 }}>Export</Button> */}
            </HeaderSection>

            {/* <FilterCard>
                <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ fontSize: 13, color: 'var(--primary-text-color)' }}>Demo: Select User Role</Text>
                </div>
                <Row gutter={24}>
                    <Col span={8}>
                        <div style={{ marginBottom: 8 }}><Text type="secondary">User Role</Text></div>
                        <Select defaultValue="Physician" onChange={handleUserRoleChange} style={{ width: '100%' }} size="large">
                            <Option value="Physician">Physician</Option>
                            <Option value="Coordinator">Coordinator</Option>
                        </Select>
                    </Col>
                   {
                        userRole === "Physician" ? (
                        <Col span={8}>
                        <div style={{ marginBottom: 8 }}><Text type="secondary">{userRole}</Text></div>
                        <Select style={{ width: '100%' }} placeholder="Select Physician" size="large">
                          {allPhysicians?.map((physician) => (
                            <Option
                            key={physician.id}
                            value={physician.id}
                            label={`${physician.name} - ${physician.email}`}
                          >
                            {physician.name} - {physician.email}
                          </Option>
                          ))}
                        </Select>
                    </Col>
                        ) : (
                     <Col span={8}>
                        <div style={{ marginBottom: 8 }}><Text type="secondary">{userRole}</Text></div>
                        <Select  style={{ width: '100%' }} placeholder="Select Coordinator" size="large">
                          { ccmCoordinators?.map((coordinator) => (
                            <Option
                            key={coordinator.id}
                            value={coordinator.id}
                            label={`${coordinator.name} - ${coordinator.email}`}
                          >
                            {coordinator.name} - {coordinator.email}
                          </Option>
                          ))}
                        </Select>
                    </Col>
                        )}
                    <Col span={8}>
                        <div style={{ marginBottom: 8 }}><Text type="secondary">View Scheduler</Text></div>
                        <Select defaultValue="All Schedulers" style={{ width: '100%' }} size="large">
                            <Option value="All Schedulers">All Schedulers</Option>
                            {Array.from(new Set(calendarData.map(d => d.scheduledBy))).map(name => (
                                <Option key={name} value={name}>{name}</Option>
                            ))}
                        </Select>
                    </Col>
                </Row>
            </FilterCard> */}

            {/* <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={12} md={6}>
                    <StatCard>
                        <div className="stat-title">Total Sent <MailOutlined /></div>
                        <div className="stat-value">{stats.totalSent}</div>
                        <div className="stat-footer">Videos sent this month</div>
                    </StatCard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <StatCard>
                        <div className="stat-title">Watched <EyeOutlined /></div>
                        <div className="stat-value">{stats.watched}</div>
                        <div className="stat-footer">{Math.round((stats.watched / stats.totalSent) * 100) || 0}% engagement rate</div>
                    </StatCard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <StatCard>
                        <div className="stat-title">Action Needed <ClockCircleOutlined /></div>
                        <div className="stat-value">{stats.actionNeeded}</div>
                        <div className="stat-footer">Unwatched with appointments soon</div>
                    </StatCard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <StatCard>
                        <div className="stat-title">Partial Views <EyeOutlined /></div>
                        <div className="stat-value">{stats.partialViews}</div>
                        <div className="stat-footer">Need follow-up</div>
                    </StatCard>
                </Col>
            </Row> */}

            <Card style={{ 
                borderRadius: 12, 
                border: '1px solid var(--border-color)', 
                background: 'var(--secondary-bg)',
                marginBottom: 24
            }}>
                <div style={{ marginBottom: 24 }}>
                    <Title level={3} style={{ margin: 0, color: 'var(--primary-text-color)' }}>Filters</Title>
                    <Text type="secondary">Search and filter video assignments</Text>
                </div>
                
                <Row gutter={24} align="bottom">
                    <Col flex="auto">
                        <div style={{ marginBottom: 8 }}><Text type="secondary">Search Patients or Videos</Text></div>
                        <Input 
                            prefix={<SearchOutlined style={{ color: 'var(--secondary-text-color)' }} />} 
                            placeholder="Search by patient name or video title..." 
                            size="large"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ borderRadius: 8 }}
                        />
                    </Col>
                    <Col style={{ width: 240 }}>
                        <div style={{ marginBottom: 8 }}><Text type="secondary">Watch Status</Text></div>
                        <Select 
                            value={statusFilter} 
                            onChange={setStatusFilter}
                            style={{ width: '100%' }} 
                            size="large"
                        >
                            <Option value="All Statuses">All Statuses</Option>
                            <Option value="true">Watched</Option>
                            {/* <Option value="Partially Watched">Partially Watched</Option> */}
                            <Option value="false">Not Watched</Option>
                        </Select>
                    </Col>
                    <Col>
                        <div style={{ marginBottom: 8 }}><Text type="secondary">View Mode</Text></div>
                        <ViewToggle>
                            <div 
                                className={`toggle-btn ${viewMode === 'calendar' ? 'active' : ''}`}
                                onClick={() => setViewMode('calendar')}
                            >
                                <CalendarOutlined /> Calendar
                            </div>
                            <div 
                                className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                                onClick={() => setViewMode('list')}
                            >
                                <UnorderedListOutlined /> List
                            </div>
                        </ViewToggle>
                    </Col>
                </Row>
            </Card>

            <div style={{ 
                background: 'var(--primary-bg)', 
                borderRadius: '16px', 
                padding: '24px',
                border: '1px solid var(--border-color)',
                minHeight: '600px'
            }}>
                {viewMode === 'calendar' ? (
                    <div className="premium-calendar-container">
                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <Title level={4} style={{ margin: 0, color: 'var(--primary-text-color)' }}>Interactive Calendar View</Title>
                                <Text type="secondary">View and manage appointments by date</Text>
                            </div>
                            <Space size="middle">
                                <Select
                                    value={selectedDate.year()}
                                    onChange={handleYearChange}
                                    style={{ width: 100 }}
                                    size="large"
                                >
                                    {yearOptions.map(year => (
                                        <Option key={year} value={year}>{year}</Option>
                                    ))}
                                </Select>
                                <Select
                                    value={selectedDate.month()}
                                    onChange={handleMonthChange}
                                    style={{ width: 120 }}
                                    size="large"
                                >
                                    {monthOptions.map((month, index) => (
                                        <Option key={index} value={index}>{month}</Option>
                                    ))}
                                </Select>
                                <ReminderButton 
                                    icon={<LeftOutlined />} 
                                    onClick={handlePreviousMonth}
                                    size="large"
                                    style={{ borderRadius: 8 }}
                                >
                                    Previous
                                </ReminderButton>
                                <Text strong style={{ fontSize: 16, color: 'var(--primary-text-color)', minWidth: 150, textAlign: 'center' }}>
                                    {selectedDate.format('MMMM YYYY')}
                                </Text>
                                <ReminderButton 
                                    icon={<RightOutlined />} 
                                    onClick={handleNextMonth}
                                    size="large"
                                    style={{ borderRadius: 8 }}
                                    iconPosition="end"
                                >
                                    Next
                                </ReminderButton>
                            </Space>
                        </div>
                        <Calendar 
                            value={selectedDate}
                            onChange={setSelectedDate}
                            dateCellRender={dateCellRender} 
                            fullscreen={true}
                            className="custom-antd-calendar"
                            headerRender={() => null}
                        />
                    </div>
                ) : (
                    <div>
                        <div style={{ marginBottom: 24 }}>
                            <Title level={4} style={{ margin: 0, color: 'var(--primary-text-color)' }}>List View</Title>
                            <Text type="secondary">Detailed patient and video tracking information</Text>
                        </div>
                        <StyledTable 
                            columns={columns} 
                            dataSource={filteredData} 
                            pagination={{ pageSize: 10, position: ['bottomRight'] }}
                            rowKey={(record) => record.key}
                            loading={loading}
                        />
                    </div>
                )}
            </div>

           
        </DashboardWrapper>

        </>
    );
};

export default CalendarPage;

